import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { TeamWithMembersData, UserLiteData } from '../../../../api/ApiTypes';
import { Colors, remInPx } from '../../../../ui';
import { DropMenuButton } from '../../../../ui/MenuButton';
import { Tip } from '../../../../ui/Tip';
import { Popover } from '../../../canvas/ui/Popover';
import { Avatar } from '../../../common/Avatar';
import { DataTable } from '../../../common/DataTable';
import { SortMode } from '../../../common/Sorter';
import { Copy, Flexer } from '../../../dashboard/elements/LayoutElements';
import { TextCell } from '../common/TableElements';
import { TeamActionMenu } from './TeamActionMenu';
import { TeamMemberInfoView } from './TeamMemberInfoView';

const MemberListItem = ({ user, team }: { user: UserLiteData, team: TeamWithMembersData }) => {
    const [open, setOpen] = useState(false);
    const onClose = useCallback(() => setOpen(false), []);

    return <Tip text={user.name} rest={1} disabled={open}>
        <Popover
            content={<TeamMemberInfoView {...{ user, team, onClose }} />}
            open={open}
            placement="bottom-start"
            inner={{ direction: 'down', offset: '.5rem' }}
            onOpenChange={setOpen}
        >
            <Avatar.ForUser user={user} clickable size="1.5" />
        </Popover>
    </Tip>;
};

const MemberListCell = ({ value }: { value: TeamWithMembersData }) => {
    const { members } = value;
    const max = 20;
    const visible = members.slice(0, max);
    const hidden = members.slice(max);
    return <Flexer flex="row" gap=".125">
        {visible.map(x => <MemberListItem key={x.id} user={x} team={value} />)}
        {hidden.length > 0 && <Tip text={`+${hidden.length}`} rest={1} disabled={true}>
            <Avatar
                name={`+${hidden.length}`}
                color={Colors.primary}
                size="1.5"
                clickable
            />
        </Tip>}
    </Flexer>;
};

const ActionsCell = ({ value }: { value: TeamWithMembersData }) => {
    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);
    return <Flexer flex="row">
        <DropMenuButton icon="deqo://icons/hero/dots-horizontal" open={open} setOpen={setOpen}>
            <Flexer minWidth="10">
                <TeamActionMenu team={value} onClose={setClosed} />
            </Flexer>
        </DropMenuButton>
    </Flexer>;
};

const columnHelper = createColumnHelper<TeamWithMembersData>();

const columns = [
    columnHelper.display({
        id: 'select',
        size: remInPx(1),
        header: ({ table }) => (
            <span></span>
            // <Checkbox
            //     checked={
            //         table.getIsAllPageRowsSelected()
            //     }
            //     onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            // />
        ),
        cell: ({ row }) => (
            <span></span>
            // <Checkbox
            //     checked={row.getIsSelected()}
            // />
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.label, {
        id: 'label',
        size: remInPx(24),
        header: () => <span>Name</span>,
        cell: info => <TextCell value={info.getValue()} />,
    }),
    columnHelper.accessor(row => row!, {
        id: 'members',
        header: () => <span>Members</span>,
        cell: info => <MemberListCell value={info.getValue()} />,
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.boardCount, {
        id: 'boardCount',
        header: () => <span>Boards</span>,
        cell: info => <TextCell value={info.getValue()} />,
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        size: remInPx(12),
        header: () => <span>Created</span>,
        cell: info => {
            return <Tip rest={50} text={info.getValue()?.toLocaleString()}>
                <Copy size="0.9375" flush>{formatDistanceToNow(info.getValue())} ago</Copy>
            </Tip>;
        }
    }),
    columnHelper.accessor(row => row.id, {
        id: 'id',
        size: remInPx(12),
        header: () => <span>Team ID</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row, {
        id: 'actions',
        size: remInPx(3),
        header: () => <span></span>,
        cell: info => <ActionsCell value={info.getValue()} />,
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

export const TeamTableView = ({ items, onSort }: { items: TeamWithMembersData[], onSort: (sort: SortMode) => void }) => {

    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
        manualSorting: true,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    onSort([sorting[0].id, sorting[0].desc ? 'desc' : 'asc']);
                }
            }
        },
    });

    // useEffect(() => {
    //     table.toggleAllRowsSelected(false);
    // }, [data]);

    // const selection = useSelection(table);

    return <DataTable table={table} selectFromFirstCellOnly />;
};

